import { useEffect, useState } from "react";
import { axiosInstance, subscriptionService } from "@miplatform/api";
import {
  INTERACTION_REQUIRED_ERROR,
  NOT_FOUND_ERROR,
  UNABLE_TO_LOGIN,
} from "@miplatform/constants";
import { useAppInsights } from "@miplatform/providers";
import {
  InteractionRequiredAuthError,
  InteractionStatus,
} from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { ErrorPage, LoadingPage } from "@mibuildweb/components";
import { usePermissions, useRoleIds, useSession } from "@mibuildweb/hooks";
import { setAppPermissions } from "./msal";

const azureAdErrorCodeForgotPassword = "AADB2C90118";

export function AuthWrapper({ children, authConfig = {} }) {
  const { authScopes, domain, loginRequest } = authConfig;
  const [loginFailed, setLoginFailed] = useState(false);
  const [loginMessage, setLoginMessage] = useState();

  const { instance, inProgress } = useMsal();

  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!instance.getActiveAccount()
  );

  const { getAppInsightsConnectionString } = useAppInsights();

  const { permissions, setPermissions: setStorePermissions } = usePermissions();
  const {
    session,
    setSession,
    setIsAuthenticated: setStoreAuthenticated,
  } = useSession();
  const { roleIds, setRoleIds } = useRoleIds();

  const bypassAuth = process.env.BYPASS_AUTH;
  const localDomain = session?.domain ?? process.env.LOCAL_DOMAIN;

  const localTenantId = session?.tenantId ?? process.env.LOCAL_TENANT_ID;
  const localUserId = session?.userId ?? process.env.LOCAL_USER_ID;

  useEffect(() => {
    const handleRedirect = async () => {
      try {
        const response = await instance.handleRedirectPromise();

        if (response) {
          instance.setActiveAccount(response.account);
        }
      } catch (error) {
        if (error) {
          setLoginFailed(true);
          if (error instanceof InteractionRequiredAuthError) {
            setLoginMessage(INTERACTION_REQUIRED_ERROR);
          }
          setLoginMessage(UNABLE_TO_LOGIN);
        }
      }
    };

    handleRedirect();
  }, []);

  useEffect(() => {
    const authHandler = async () => {
      try {
        if (inProgress !== InteractionStatus.None) {
          return;
        }

        const account = instance.getActiveAccount();

        if (bypassAuth) {
          const oid = localUserId;

          const { data: userData } = await subscriptionService.userProfile.get(
            oid,
            localDomain
          );

          setSession({
            userId: userData?.appUserId,
            tenantId: userData?.appTenantId,
            domain: localDomain,
            isAuthenticated: true,
          });
          setStorePermissions(userData?.permissions);
          setRoleIds(userData?.roleIds);

          return;
        }

        if (account) {
          setIsAuthenticated(true);
          await setAppPermissions({
            msalInstance: instance,
            authScopes,
            inProgress,
            setPermissions: setStorePermissions,
          });

          return;
        }

        // Redirect to login
        if (!bypassAuth) {
          setIsAuthenticated(false);
          await instance.loginRedirect(loginRequest);
        }
      } catch (error) {
        if (error) {
          if (
            error?.errorMessage?.includes(azureAdErrorCodeForgotPassword) &&
            inProgress === InteractionStatus.None
          ) {
            const resetPasswordRequest = {
              authority: domain.forgotPasswordAuthority,
              scopes: [],
            };
            await instance.loginRedirect(resetPasswordRequest);
            return;
          }
          if (error?.errorMessage?.includes(NOT_FOUND_ERROR)) {
            setLoginFailed(true);
            setLoginMessage(UNABLE_TO_LOGIN);
            return;
          }
        }
        setLoginFailed(true);
      }
    };
    authHandler();
  }, [instance, inProgress, session?.userId]);

  const bypassAuthInterceptor = () => {
    axiosInstance.interceptors.request.use(
      async (request) => {
        const apiRequest = request;
        apiRequest.headers.BypassAuth = true;

        apiRequest.headers["MV-RoleIds"] = roleIds.join(",");
        apiRequest.headers["MV-UserId"] = localUserId;
        apiRequest.headers["MV-TenantId"] = localTenantId;
        apiRequest.headers["MV-Permissions"] = permissions;

        return apiRequest;
      },
      (err) => Promise.reject(err)
    );
  };

  const authInterceptor = () => {
    axiosInstance.interceptors.request.use(
      async (request) => {
        const apiRequest = request;
        if (!apiRequest.headers.noToken) {
          const account = instance.getActiveAccount();
          if (!account && inProgress === InteractionStatus.None) {
            await instance.loginRedirect(loginRequest);
          }

          const authRequest = {
            scopes: authScopes,
            account,
            extraQueryParameters: {
              domain_hint: domain.domain_hint,
            },
          };
          try {
            const tokenSilentResponse = await instance.acquireTokenSilent(
              authRequest
            );

            const { accessToken, idTokenClaims } = tokenSilentResponse;
            setSession({
              userId: idTokenClaims.mip_user,
              tenantId: idTokenClaims.mip_tenant,
            });
            apiRequest.headers.Authorization = `Bearer ${accessToken}`;
          } catch (error) {
            if (
              error instanceof InteractionRequiredAuthError &&
              inProgress === InteractionStatus.None
            ) {
              instance.acquireTokenRedirect(authRequest);
            }
          }
        }

        return apiRequest;
      },
      (err) => Promise.reject(err)
    );
  };

  useEffect(() => {
    const setInterceptor = bypassAuth ? bypassAuthInterceptor : authInterceptor;
    setInterceptor();
  }, []);

  useEffect(() => {
    if (bypassAuth) return;

    const initialize = async () => {
      if (!isAuthenticated) {
        return;
      }
      getAppInsightsConnectionString();
    };
    initialize();
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      setStoreAuthenticated(isAuthenticated);
    }
  }, [isAuthenticated]);

  if (bypassAuth) {
    return children;
  }

  if (isAuthenticated) {
    return children;
  }

  if (isAuthenticated) {
    return <LoadingPage />;
  }

  if (loginFailed) {
    return <ErrorPage error={loginMessage} />;
  }

  return <LoadingPage />;
}
